/**
 * Wat focus kan krijgen, in de volgorde waarin een invuller het zou bereiken.
 *
 * `[contenteditable="true"]` staat erbij voor de bewerkbare laag van een rich-text-veld, en een
 * uitgeschakeld element staat er niet bij: een link die op een knop landt die niets doet, laat de
 * invuller op een dood punt achter.
 */
const FOCUSABLE = [
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

function canFocus(element: Element): element is HTMLElement {
  return element instanceof HTMLElement && element.matches(FOCUSABLE)
}

/**
 * Geeft de focus aan het invoerelement binnen een veldschil of een sectie (R-140).
 *
 * Het doel van een link in het overzicht van openstaande controles is de schil om het veld
 * (`FieldShell` met `anker`), want het id van de control zelf komt uit `useId()`. Binnen die schil
 * gaat de focus naar de control, en kan die zelf geen focus krijgen (een `fieldset` met
 * radioknoppen, de `div` van een herhaalgroep), dan naar het eerste element erin dat dat wel kan:
 * de eerste optie, of de knop "Rij toevoegen" bij een groep zonder rijen. Bij een sectie is dat de
 * knop in haar kop, want een bevinding op sectieniveau hoort bij geen enkel veld.
 *
 * De control vinden gaat op de vorm van zijn id (`<useId>-<veld-id>`, zie `FieldShell`): het
 * label, de hulptekst en de opmerkingknop staan eerder in de schil en zijn niet waar de invuller
 * heen moet.
 *
 * Geeft terug of er iets de focus kreeg.
 */
export function focusInField(target: HTMLElement): boolean {
  const fieldId = target.dataset['veld']
  const control =
    fieldId === undefined
      ? undefined
      : [...target.querySelectorAll('[id]')].find((element) => element.id.endsWith(`-${fieldId}`))
  const candidate =
    control === undefined
      ? (target.querySelector('.ef-sectie__knop') ?? target.querySelector(FOCUSABLE))
      : canFocus(control)
        ? control
        : (control.querySelector(FOCUSABLE) ?? target.querySelector(FOCUSABLE))
  if (!(candidate instanceof HTMLElement)) return false
  candidate.focus({ preventScroll: true })
  /*
   * In één keer en niet zacht (audit van 30 oktober 2026, WCAG 2.4.11). Een rich-text-editor
   * schuift bij focus zelf zijn cursor in beeld, en wel tot net binnen de onderrand van het
   * venster; liep er op dat moment nog een zachte scroll, dan brak die af en bleef het veld achter
   * de actiebalk die onderaan plakt. Staat het veld al in het midden, dan doet de editor niets.
   */
  candidate.scrollIntoView({ behavior: 'auto', block: 'center' })
  return document.activeElement === candidate
}
